import { useRef, useState, type KeyboardEvent, type PointerEvent } from "react";
import { QUADRO_DA_FACE, regiaoNoPonto, type MarcacaoFacial, type ModeloDaFace, type TamanhoDaMarcacao, type TipoDeMarcacao, type VistaDaFace } from "@bookdental/schemas";
import { Button } from "@bookdental/ui";
import { cn } from "@/lib/cn";
import { imagemDaFace } from "./desenhos";
import { FormaDaMarcacao, QUADRO_VIEWBOX, type EstiloDaMarcacao } from "./FormaDaMarcacao";
import { alcasDa, arrastar, deslocar, iniciarArraste, limitar, novaMarcacao, type Alca, type Arraste } from "./geometria";

const W = QUADRO_DA_FACE.largura;
const H = QUADRO_DA_FACE.altura;

export type MarcacaoDeOutroItem = { itemId: string; nome: string; cor: string; m: MarcacaoFacial; estilo: EstiloDaMarcacao };
export type Ferramenta = { tipo: TipoDeMarcacao; tamanho: TamanhoDaMarcacao };

/**
 * O desenho de uma vista com as marcações. As do item em montagem (`proprias`) se criam no clique,
 * se arrastam e têm alças (raio, semieixos, giro, vértices); as dos outros itens só destacam o item
 * ao clicar. O trajeto se desenha clicando ponto a ponto e fecha com Enter, duplo clique ou o botão.
 */
export function EditorDaFace({
  modelo,
  vista,
  editavel,
  outras,
  proprias,
  cor,
  ferramenta,
  selecionada,
  onSelecionar,
  onMudar,
  onClicarItem,
}: {
  modelo: ModeloDaFace;
  vista: VistaDaFace;
  editavel: boolean;
  outras: MarcacaoDeOutroItem[];
  proprias: MarcacaoFacial[];
  cor: string;
  ferramenta: Ferramenta;
  selecionada: number | null;
  onSelecionar: (i: number | null) => void;
  onMudar: (lista: MarcacaoFacial[]) => void;
  onClicarItem: (itemId: string) => void;
}) {
  const svg = useRef<SVGSVGElement>(null);
  const arraste = useRef<Arraste | null>(null);
  const [rascunho, setRascunho] = useState<[number, number][] | null>(null);

  const ponto = (e: { clientX: number; clientY: number }): [number, number] => {
    const el = svg.current!;
    const p = el.createSVGPoint();
    p.x = e.clientX;
    p.y = e.clientY;
    const q = p.matrixTransform(el.getScreenCTM()!.inverse());
    return [limitar(q.x / W), limitar(q.y / H)];
  };
  const trocar = (i: number, m: MarcacaoFacial) => onMudar(proprias.map((x, k) => (k === i ? m : x)));
  const concluirTrajeto = () => {
    // O duplo clique que fecha o trajeto também marca pontos repetidos no fim: saem aqui.
    const pontos = (rascunho ?? []).filter(([x, y], k, l) => k === 0 || Math.hypot(x - l[k - 1][0], y - l[k - 1][1]) > 0.004);
    if (pontos.length >= 2) {
      onMudar([...proprias, { tipo: "TRAJETO", vista, pontos, regiao: regiaoNoPonto(vista, ...pontos[0])?.codigo ?? null }]);
      onSelecionar(proprias.length);
    }
    setRascunho(null);
  };

  const noFundo = (e: PointerEvent) => {
    if (!editavel) return onSelecionar(null);
    const [x, y] = ponto(e);
    if (ferramenta.tipo === "TRAJETO") return setRascunho((r) => [...(r ?? []), [x, y]]);
    onMudar([...proprias, novaMarcacao(ferramenta.tipo, ferramenta.tamanho, vista, x, y)]);
    onSelecionar(proprias.length);
  };
  const pegar = (e: PointerEvent, alca: Alca, indice: number, vertice?: number) => {
    e.stopPropagation();
    if (!editavel) return;
    onSelecionar(indice);
    arraste.current = iniciarArraste(alca, indice, proprias[indice], ponto(e), vertice);
    svg.current?.setPointerCapture(e.pointerId);
  };
  const mover = (e: PointerEvent) => {
    const a = arraste.current;
    if (a) trocar(a.indice, arrastar(a, ponto(e)));
  };
  const soltar = () => {
    arraste.current = null;
  };
  const teclado = (e: KeyboardEvent) => {
    if (e.key === "Escape") return rascunho ? setRascunho(null) : onSelecionar(null);
    if (e.key === "Enter" && rascunho) return concluirTrajeto();
    if (selecionada == null || !editavel || !proprias[selecionada]) return;
    const passo = e.shiftKey ? 0.02 : 0.005;
    const setas: Record<string, [number, number]> = { ArrowLeft: [-passo, 0], ArrowRight: [passo, 0], ArrowUp: [0, -passo], ArrowDown: [0, passo] };
    if (setas[e.key]) {
      e.preventDefault();
      trocar(selecionada, deslocar(proprias[selecionada], ...setas[e.key]));
    } else if (e.key === "Delete" || e.key === "Backspace") {
      e.preventDefault();
      onMudar(proprias.filter((_, k) => k !== selecionada));
      onSelecionar(null);
    }
  };

  const daVista = proprias.map((m, i) => ({ m, i })).filter(({ m }) => m.vista === vista);
  const sel = selecionada != null && proprias[selecionada]?.vista === vista ? proprias[selecionada] : null;
  return (
    <div className="relative">
      <svg
        ref={svg}
        viewBox={QUADRO_VIEWBOX}
        tabIndex={0}
        aria-label="Desenho da face"
        onKeyDown={teclado}
        onPointerMove={mover}
        onPointerUp={soltar}
        onPointerCancel={soltar}
        onDoubleClick={() => rascunho && concluirTrajeto()}
        className={cn("block h-auto w-full select-none rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-brand-300", editavel && "touch-none")}
      >
        <image href={imagemDaFace(modelo, vista)} width={W} height={H} className="pointer-events-none dark:invert" />
        <rect width={W} height={H} fill="transparent" onPointerDown={noFundo} className={editavel ? "cursor-crosshair" : undefined} />
        {outras
          .filter((o) => o.m.vista === vista)
          .map((o, k) => (
            <g key={`o${k}`} className="cursor-pointer" onPointerDown={(e) => e.stopPropagation()} onClick={() => onClicarItem(o.itemId)}>
              <title>{o.nome}</title>
              <FormaDaMarcacao m={o.m} cor={o.cor} estilo={o.estilo} />
            </g>
          ))}
        {daVista.map(({ m, i }) => (
          <g
            key={`p${i}`}
            role="button"
            aria-label={`Marcação ${i + 1}`}
            className={editavel ? "cursor-move" : undefined}
            onPointerDown={(e) => pegar(e, "mover", i)}
          >
            <FormaDaMarcacao m={m} cor={cor} estilo={selecionada === i ? "destaque" : "normal"} />
          </g>
        ))}
        {sel && editavel &&
          alcasDa(sel).map((h, k) => (
            <g key={`h${k}`} className="cursor-pointer" onPointerDown={(e) => pegar(e, h.alca, selecionada!, h.vertice)}>
              <circle cx={h.x} cy={h.y} r={14} fill="transparent" />
              <circle cx={h.x} cy={h.y} r={5.5} fill="#fff" stroke={h.alca === "giro" ? "#2457FF" : cor} strokeWidth={2} />
            </g>
          ))}
        {rascunho && (
          <polyline points={rascunho.map(([x, y]) => `${x * W},${y * H}`).join(" ")} fill="none" stroke={cor} strokeWidth={4} strokeDasharray="8 6" strokeLinecap="round" />
        )}
        {rascunho?.map(([x, y], k) => <circle key={`r${k}`} cx={x * W} cy={y * H} r={4} fill={cor} />)}
      </svg>
      {rascunho && (
        <div className="absolute inset-x-2 bottom-2 flex items-center justify-between gap-2 rounded-md border border-zinc-200 bg-white px-2 py-1.5 text-[11.5px] shadow-lg dark:border-border dark:bg-card">
          <span className="text-zinc-600 dark:text-muted-foreground">
            {rascunho.length < 2 ? "Clique os pontos do trajeto" : `${rascunho.length} pontos`}
          </span>
          <span className="flex gap-1.5">
            <Button type="button" size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => setRascunho(null)}>
              Cancelar
            </Button>
            <Button type="button" size="sm" className="h-7 px-2 text-xs" disabled={rascunho.length < 2} onClick={concluirTrajeto}>
              Concluir trajeto
            </Button>
          </span>
        </div>
      )}
    </div>
  );
}
